import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Loader2,
  PackageCheck,
  RotateCcw,
  Search,
  ShoppingBag,
} from 'lucide-react';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import type { SearchResultItem } from '@/services/api/search';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_ICONS: Record<string, typeof RotateCcw> = {
  return: RotateCcw,
  delivery: PackageCheck,
  delivery_note: FileText,
  item: ShoppingBag,
};

const TYPE_LABEL_KEYS: Record<string, string> = {
  return: 'search.typeReturn',
  delivery: 'search.typeDelivery',
  delivery_note: 'search.typeDeliveryNote',
  item: 'search.typeItem',
};

function groupByType(results: SearchResultItem[]) {
  const groups: Record<string, SearchResultItem[]> = {};
  for (const item of results) {
    if (!groups[item.type]) groups[item.type] = [];
    groups[item.type].push(item);
  }
  return groups;
}

function highlightMatch(text: string, query: string) {
  if (!query || query.length < 2) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary/30 text-inherit rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const { data, isFetching, isError } = useGlobalSearch(debouncedQuery);
  const results = data?.results ?? [];
  const hasContent =
    (inputValue.length > 0 && inputValue.trim().length < 2) ||
    (isError && debouncedQuery.length >= 2) ||
    (!isFetching && debouncedQuery.length >= 2 && results.length === 0) ||
    results.length > 0;

  // Debounce input value
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(inputValue.trim()), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Reset state on open, clear cache on close
  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      setDebouncedQuery('');
      setActiveIndex(0);
      queryClient.removeQueries({ queryKey: ['globalSearch'] });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, queryClient]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [results.length]);

  // Close on click outside (only if this instance is visible)
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      // Skip if this instance is hidden (e.g. inside a display:none parent on mobile)
      if (containerRef.current.offsetParent === null && getComputedStyle(containerRef.current).position !== 'fixed') return;
      if (!containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose]);

  // Navigate to result
  const openResult = useCallback(
    (item: SearchResultItem) => {
      onClose();
      navigate(item.url);
    },
    [navigate, onClose],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % Math.max(results.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
      } else if (e.key === 'Enter' && results[activeIndex]) {
        e.preventDefault();
        openResult(results[activeIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [results, activeIndex, openResult, onClose],
  );

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!isOpen) return null;

  const grouped = groupByType(results);
  const typeOrder = ['return', 'delivery', 'delivery_note', 'item'];
  let flatIndex = -1;

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      {/* Search Input - inline in the nav bar */}
      <div className="relative flex items-center">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-base-content/40 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t('search.placeholder')}
          className="min-h-12 w-full lg:w-72 rounded-btn border border-primary bg-base-100 pl-9 pr-9 text-sm outline-none transition-colors"
          autoComplete="off"
          spellCheck={false}
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-base-content/40 animate-spin" />
        )}
      </div>

      {/* Dropdown Results */}
      {hasContent && (
        <div className="absolute left-0 right-0 lg:left-auto lg:right-0 top-full mt-1 lg:w-[28rem] rounded-card border border-go-border bg-base-200 shadow-2xl overflow-hidden animate-fade-in z-[60]">
          <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
            {/* Min chars hint */}
            {inputValue.length > 0 && inputValue.trim().length < 2 && (
              <div className="px-4 py-6 text-center text-sm text-base-content/50">
                {t('search.minChars')}
              </div>
            )}

            {/* Error state */}
            {isError && debouncedQuery.length >= 2 && (
              <div className="px-4 py-6 text-center text-sm text-error">
                {t('search.error')}
              </div>
            )}

            {/* No results */}
            {!isFetching &&
              !isError &&
              debouncedQuery.length >= 2 &&
              results.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-base-content/50">
                  {t('search.noResults', { query: debouncedQuery })}
                </div>
              )}

            {/* Grouped results */}
            {typeOrder.map((type) => {
              const items = grouped[type];
              if (!items || items.length === 0) return null;
              const Icon = TYPE_ICONS[type] ?? Search;

              return (
                <div key={type}>
                  <div className="px-4 pt-3 pb-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-base-content/50">
                      {t(TYPE_LABEL_KEYS[type])}
                    </span>
                  </div>
                  {items.map((item) => {
                    flatIndex++;
                    const isActive = flatIndex === activeIndex;
                    const currentIndex = flatIndex;

                    return (
                      <button
                        key={`${item.type}-${item.id}`}
                        data-active={isActive}
                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-base-content hover:bg-base-300'
                        }`}
                        onClick={() => openResult(item)}
                        onMouseEnter={() => setActiveIndex(currentIndex)}
                      >
                        <Icon className="h-4 w-4 shrink-0 opacity-60" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">
                            {highlightMatch(item.title, debouncedQuery)}
                          </div>
                          {item.subtitle && (
                            <div className="truncate text-xs text-base-content/50">
                              {highlightMatch(item.subtitle, debouncedQuery)}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {results.length > 0 && (
            <div className="flex items-center gap-4 border-t border-go-border px-4 py-2 text-xs text-base-content/40">
              <span>
                <kbd className="kbd kbd-xs">↑↓</kbd> {t('search.footerNav')}
              </span>
              <span>
                <kbd className="kbd kbd-xs">↵</kbd> {t('search.footerOpen')}
              </span>
              <span>
                <kbd className="kbd kbd-xs">Esc</kbd> {t('search.footerClose')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
